import { z } from 'zod';

import { zPhase5bTier1PropertyScopedId, zPhase5bTier1UnitScopedIdNullish } from '../common/phase5bTier1Ids';
import { TICKET_STATUS_ACTIVE, TICKET_STATUS_COMPLETED, type WorkOrderStatus } from '../common/statuses';

/**
 * Zod schemas for maintenance-related types
 * These provide runtime validation and type inference
 */

/** All work order statuses; single source of truth with common/statuses (includes Cancelled). */
const ticketStatusEnum = [...TICKET_STATUS_ACTIVE, ...TICKET_STATUS_COMPLETED] as const;

// TurnTask schema (from existing schemas.ts)
export const turnTaskSchema = z.object({
  id: z.string(),
  turnId: z.string(),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  status: z.enum(['Not Started', 'In Progress', 'Completed'] as const),
  assignedVendorId: z.string().optional(),
  dueDate: z.string().optional(),
  completedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Export schema only, type is in turns.ts

// Partial schema for updates
export const turnTaskUpdateSchema = turnTaskSchema.partial().required({ id: true });
export type TurnTaskUpdate = z.infer<typeof turnTaskUpdateSchema>;

// WorkOrderLineItem schema
const workOrderLineItemSchema = z.object({
  id: z.string(),
  description: z.string(),
  quantity: z.number().nonnegative(),
  unitPrice: z.number().nonnegative(),
  total: z.number().nonnegative(),
  isLabor: z.boolean(),
});

// MaintenanceTicket schema (API/Prisma: coerce status, priority, source, approvalStatus, dates)
const ticketPriorityEnum = ['Low', 'Medium', 'High', 'Critical', 'Urgent'] as const;
const ticketSourceEnum = ['PM', 'Tenant', 'Vendor'] as const;
const approvalStatusEnum = ['Pending', 'Approved', 'Rejected'] as const;
const ticketDateLike = z.union([z.string(), z.date()]).transform(v => (v == null ? '' : v instanceof Date ? v.toISOString() : String(v)));

export const maintenanceTicketSchema = z.object({
  id: z.string(),
  title: z.string().transform(t => (t?.trim() || 'Work Order')),
  description: z.string().default(''),
  status: z.string().transform(s => (ticketStatusEnum.includes(s as WorkOrderStatus) ? (s as WorkOrderStatus) : 'Open')),
  priority: z.string().transform(p => (ticketPriorityEnum.includes(p as (typeof ticketPriorityEnum)[number]) ? p as (typeof ticketPriorityEnum)[number] : 'Medium')),
  propertyId: zPhase5bTier1PropertyScopedId,
  unitId: zPhase5bTier1UnitScopedIdNullish,
  tenantId: z.string().nullish(),
  source: z.string().optional().nullable().transform(v => (v && ticketSourceEnum.includes(v as (typeof ticketSourceEnum)[number]) ? (v as (typeof ticketSourceEnum)[number]) : undefined)),
  createdAt: ticketDateLike.default(''),
  date: z.union([z.string(), z.date()]).optional().nullable().transform(v => (v == null ? undefined : v instanceof Date ? (v).toISOString().split('T')[0] : String(v))),
  completedAt: z.string().nullish(),
  assignedVendorId: z.string().nullish(),
  billId: z.string().nullish(),
  images: z.array(z.string()).nullish().default([]),
  aiAnalysis: z.string().nullish(),
  estimatedCostCents: z.number().int().nonnegative().nullish(),
  lineItems: z.array(workOrderLineItemSchema).nullish(),
  category: z.string().nullish(),
  actualCostCents: z.number().int().nonnegative().nullish(),
  sopId: z.string().nullish(),
  checklist: z.array(z.object({
    id: z.string(),
    text: z.string(),
    completed: z.boolean(),
  })).nullish(),
  requiresApproval: z.boolean().nullish(),
  approvalThreshold: z.number().nonnegative().nullish(),
  approvalThresholdCents: z.number().int().nonnegative().nullish(),
  approvalRequiredAmountCents: z.number().int().nonnegative().nullish(),
  estimateRevision: z.number().int().nonnegative().nullish(),
  decisionEstimateRevision: z.number().int().nonnegative().nullish(),
  approvalStatus: z.string().optional().nullable().transform(v => (v && approvalStatusEnum.includes(v as (typeof approvalStatusEnum)[number]) ? (v as (typeof approvalStatusEnum)[number]) : v === null ? null : undefined)),
  approvedAt: z.union([z.string(), z.date(), z.null()]).nullish().transform(v => (v == null ? v : v instanceof Date ? v.toISOString() : String(v))),
  approvedBy: z.string().nullish(),
  approvedByUserId: z.string().nullish(),
  approvalNotes: z.string().nullish(),
  rejectedAt: z.union([z.string(), z.date(), z.null()]).nullish().transform(v => (v == null ? v : v instanceof Date ? v.toISOString() : String(v))),
  rejectedBy: z.string().nullish(),
  rejectedByUserId: z.string().nullish(),
  rejectionNotes: z.string().nullish(),
  rating: z.number().min(1).max(5).nullish(),
}).passthrough();

// Infer TypeScript type from schema
// Export schema only, type is in workOrders.ts

// Partial schema for updates
export const maintenanceTicketUpdateSchema = maintenanceTicketSchema.partial().required({ id: true });
export type MaintenanceTicketUpdate = z.infer<typeof maintenanceTicketUpdateSchema>;

// Project schema
export const projectSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Project name is required'),
  description: z.string(),
  status: z.enum(['Planning', 'In Progress', 'Completed', 'On Hold'] as const),
  progress: z.number().min(0).max(100),
  budget: z.number().nonnegative(),
  spent: z.number().nonnegative(),
  startDate: z.string(),
  targetDate: z.string(),
  completedDate: z.string().nullish(),
  taskCount: z.number().nonnegative(),
  priority: z.enum(['Low', 'Medium', 'High'] as const),
  propertyId: z.string().nullish(),
  sopId: z.string().nullish(),
  totalPhases: z.number().nonnegative().nullish(),
  currentPhase: z.number().nonnegative().nullish(),
}).passthrough();

// Infer TypeScript type from schema
// Export schema only, type is in projects/index.ts

// Partial schema for updates
export const projectUpdateSchema = projectSchema.partial().required({ id: true });
export type ProjectUpdate = z.infer<typeof projectUpdateSchema>;

// SOP step schema
const sopStepSchema = z.object({
  id: z.string(),
  text: z.string(),
  required: z.boolean(),
  order: z.number().int().optional(),
  assignedRole: z.enum(['PM', 'Vendor', 'Tenant', 'Inspector'] as const).optional(),
  estimatedTime: z.number().int().optional(),
  requiredDocuments: z.array(z.string()).optional(),
});

// StandardOperatingProcedure (SOP) schema
export const sopSchema = z.object({
  id: z.string(),
  title: z.string().min(1, 'SOP title is required'),
  category: z.enum(['Maintenance', 'Turnover', 'Inspection', 'Emergency', 'Compliance', 'Tenant Services', 'Vendor Management', 'Financial', 'Project'] as const),
  subcategory: z.string().nullish(),
  description: z.string(),
  propertyTypes: z.array(z.enum(['residential', 'commercial', 'mixed-use'] as const)).nullish(),
  estimatedDuration: z.number().int().nullish(),
  requiredVendorType: z.string().nullish(),
  priority: z.enum(['routine', 'urgent', 'emergency'] as const).nullish(),
  steps: z.array(sopStepSchema),
  autoCreateWorkOrder: z.boolean().nullish(),
  notifyTenant: z.boolean().nullish(),
  notifyOwner: z.boolean().nullish(),
  requiredDocuments: z.array(z.string()).nullish(),
  timesUsed: z.number().int().nonnegative().nullish(),
  averageCompletionTime: z.number().int().nullish(),
  successRate: z.number().min(0).max(100).nullish(),
  lastUsed: z.string().nullish(),
  regulatoryRequirement: z.string().nullish(),
  reviewFrequency: z.enum(['monthly', 'quarterly', 'annually'] as const).nullish(),
  lastReviewed: z.string().nullish(),
  estimatedCost: z.object({
    min: z.number().nonnegative(),
    max: z.number().nonnegative(),
  }).nullish(),
  estimatedHours: z.number().nonnegative().nullish(),
  requiredTools: z.array(z.string()).nullish(),
  createdBy: z.string().nullish(),
  isGlobal: z.boolean().nullish(),
  version: z.number().int().nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
}).passthrough();

// Export schema only, type is in maintenance/index.ts? No, let's check where SOP is defined.

// SOPInstance step progress schema
const sopInstanceStepProgressSchema = z.object({
  stepId: z.string(),
  completed: z.boolean(),
  completedBy: z.string().optional(),
  completedAt: z.string().optional(),
  notes: z.string().optional(),
  deviation: z.string().optional(),
});

// SOPInstance schema
export const sopInstanceSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  workOrderId: z.string().nullish(),
  projectId: z.string().nullish(),
  projectPhaseId: z.string().nullish(),
  unitTurnId: z.string().nullish(),
  inspectionId: z.string().nullish(),
  status: z.enum(['in_progress', 'completed', 'failed', 'cancelled'] as const),
  startedAt: z.string(),
  completedAt: z.string().nullish(),
  startedBy: z.string(),
  stepProgress: z.array(sopInstanceStepProgressSchema),
  deviations: z.string().nullish(),
  outcome: z.enum(['successful', 'needs_revision', 'escalated', 'partial'] as const).nullish(),
  completionPercentage: z.number().int().min(0).max(100),
}).passthrough();

// Export schema only
